import React from 'react';
import { Loader, useToasts } from 'freemium-ui';
import { useTranslation } from 'react-i18next';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';
import { SEGMENTS_LIMIT } from 'routes/constants';
import { useAccessControl } from 'utils/access-control';
import {
  AccountInfoType,
  BreadcrumpDataType,
  ConfirmationType,
  UserType,
} from '../../../shared/types';
import { useGetContactForm } from '../../../shared/hooks';
import { Center } from '../../../shared/styles';
import Segments from './screens';
import CreateSegment from './screens/create';
import { useGetSegmentsCount } from './hooks';

type SegmentsRoutesProps = {
  setBreadcrumpData?: (
    str: BreadcrumpDataType | React.SetStateAction<BreadcrumpDataType>,
  ) => void;
  setConfirmationData: (
    str: ConfirmationType | React.SetStateAction<ConfirmationType>,
  ) => void;
  actionTime?: string;
  accountInfo?: AccountInfoType;
  userInfo?: UserType;
  contactsCount: number;
  fetchContactCount: () => void;
};

const SegmentsRoutes = (props: SegmentsRoutesProps) => {
  const { t } = useTranslation();
  const {
    setBreadcrumpData,
    actionTime,
    setConfirmationData,
    accountInfo,
    userInfo,
    contactsCount,
    fetchContactCount,
  } = props;
  const { path } = useRouteMatch();
  const { access = [] } = useAccessControl();
  const {
    data: contactForm = {},
    isLoading: isLoadingContactForm,
    isError: isContactFormError,
  } = useGetContactForm();
  const { data: segmentsCountData, isLoading: isLoadingSegmentsCount } =
    useGetSegmentsCount({ enabled: access.includes('contact:write') });

  if (isContactFormError)
    return <Center>{t('empty_state.message.network_error')}</Center>;

  return isLoadingContactForm || isLoadingSegmentsCount ? (
    <Center>
      <Loader />
    </Center>
  ) : (
    <Switch>
      <Route exact path={path}>
        <Segments
          setConfirmationData={setConfirmationData}
          contactForm={contactForm}
          setBreadcrumpData={setBreadcrumpData}
          accountInfo={accountInfo}
          userInfo={userInfo}
          contactsCount={contactsCount}
          fetchContactCount={fetchContactCount}
        />
      </Route>
      {access.indexOf('contact:write') > -1 && (
        <Route path={`${path}/create`}>
          {Number(segmentsCountData?.count) <
          (Number(accountInfo?.resource_limit_configuration?.segments) ||
            SEGMENTS_LIMIT) ? (
            <CreateSegment
              setConfirmationData={setConfirmationData}
              setBreadcrumpData={setBreadcrumpData}
              contactForm={contactForm}
              accountInfo={accountInfo}
              userInfo={userInfo}
              contactsCount={contactsCount}
              fetchContactCount={fetchContactCount}
            />
          ) : (
            <SegmentsRoutesRedirect />
          )}
        </Route>
      )}
      <Route path={`${path}/:segmentId/edit`}>
        <CreateSegment
          setConfirmationData={setConfirmationData}
          actionTime={actionTime}
          setBreadcrumpData={setBreadcrumpData}
          contactForm={contactForm}
          accountInfo={accountInfo}
          userInfo={userInfo}
          contactsCount={contactsCount}
          fetchContactCount={fetchContactCount}
        />
      </Route>
    </Switch>
  );
};

export default SegmentsRoutes;

const SegmentsRoutesRedirect = () => {
  const { t } = useTranslation();
  const [, setToast] = useToasts();
  React.useEffect(() => {
    setToast({
      type: 'warning',
      delay: 3000,
      text: `${t('forms.toast.warning.segments_limit_reached')}`,
    });
  }, []);
  return <Redirect to="/segments" />;
};
